import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

/* Admin-editable overrides for the 28 IR + 28 Capital report packs.
   Pure TEXT overrides only (profile, tagline/intro, section titles, and the text of
   paragraph/callout/bullets/steps blocks). Structure is fixed and tables/step-paths
   are never overridden. Merged in ONE place — getIrReport()/getCapitalReport() — so
   both the on-screen report and the PDF pick it up, with the hardcoded packs as the
   fallback whenever a report (or field) hasn't been edited. No scoring is touched. */

// Module-level caches, populated by useReportContent() and read by the merge fns.
let _ir: Record<string, any> = {};
let _capital: Record<string, any> = {};

export const irOverride = (key: string) => _ir[key];
export const capitalOverride = (key: string) => _capital[key];

// Merge a text override over a hardcoded report. `ov` shape mirrors the report by
// position: { profile?, tagline?, intro?, sections?: [{ title?, blocks?: [{ text?, items?, steps? }] }] }.
export function applyReportOverride<T extends Record<string, any>>(report: T, ov: any): T {
  if (!report || !ov || typeof ov !== 'object') return report;
  const out: any = { ...report };
  if (typeof ov.profile === 'string') out.profile = ov.profile;
  if (typeof ov.tagline === 'string') out.tagline = ov.tagline;
  if (typeof ov.intro === 'string') out.intro = ov.intro;
  if (Array.isArray(ov.sections) && Array.isArray(report.sections)) {
    out.sections = report.sections.map((sec: any, i: number) => {
      const so = ov.sections[i];
      if (!so || typeof so !== 'object') return sec;
      const ns: any = { ...sec };
      if (typeof so.title === 'string') ns.title = so.title;
      if (Array.isArray(so.blocks) && Array.isArray(sec.blocks)) {
        ns.blocks = sec.blocks.map((b: any, j: number) => {
          const bo = so.blocks[j];
          if (!bo || typeof bo !== 'object') return b;
          if (b.kind === 'paragraph' || b.kind === 'callout') {
            return typeof bo.text === 'string' ? { ...b, text: bo.text } : b;
          }
          if (b.kind === 'bullets') {
            return Array.isArray(bo.items) ? { ...b, items: bo.items.map((x: any) => String(x)) } : b;
          }
          if (b.kind === 'steps') {
            if (!Array.isArray(bo.items)) return b;
            return {
              ...b,
              items: b.items.map((it: any, k: number) => {
                const io = bo.items[k];
                if (!io || typeof io !== 'object') return it;
                return {
                  title: typeof io.title === 'string' ? io.title : it.title,
                  sub: Array.isArray(io.sub) ? io.sub.map((x: any) => String(x)) : it.sub,
                };
              }),
            };
          }
          return b; // table / path — never overridden
        });
      }
      return ns;
    });
  }
  return out;
}

// Subscribe to the override doc; keeps the module caches fresh. Mount this on any
// page that renders or downloads a report (IRScore, Dashboard). Returns `ready`.
export function useReportContent(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const ref = doc(db, 'reportContent', 'current');
    return onSnapshot(
      ref,
      snap => {
        const d = snap.exists() ? snap.data() : {};
        _ir = (d.ir && typeof d.ir === 'object') ? d.ir : {};
        _capital = (d.capital && typeof d.capital === 'object') ? d.capital : {};
        setReady(true);
      },
      () => setReady(true), // permission/transient error → keep defaults
    );
  }, []);
  return ready;
}
